import uFuzzy from '@leeoniya/ufuzzy';
import { MontelAccount } from 'domain/account/interfaces';
import { Company } from 'domain/company/Company';
import { useEffect, useState } from 'react';

export enum MatchType {
	DomainMatch,
	NameMatch,
	TokenMatch,
	NoMatch,
}

export interface SuggestedCompaniesMatchResult {
	companies: Company[];
	matchedOn: MatchType;
}

export interface useSuggestedCompaniesReturnType {
	suggestions: {
		companies: Company[];
		matchedOn: MatchType;
	};
	refreshSuggestions: (companyOptions: Company[]) => void;
}

export const useSuggestedCompanies = (
	companyOptions: Company[],
	account?: MontelAccount,
): useSuggestedCompaniesReturnType => {
	const [suggestions, setSuggestions] =
		useState<SuggestedCompaniesMatchResult>({
			companies: [],
			matchedOn: MatchType.NoMatch,
		});

	useEffect(() => {
		filter(companyOptions);
	}, [companyOptions, account]);

	const filter = (options: Company[]) => {
		setSuggestions({
			companies: [],
			matchedOn: MatchType.NoMatch,
		});

		if (!account || !options || options.length === 0) {
			return;
		}

		// Filter companies whose email domain(s) match the account's email domain
		const accountEmailDomain = account?.email?.value.split('@')[1];
		const domainMatchCompanies = options.filter((company) =>
			company.emailDomains.some((domain) =>
				domain
					.toLowerCase()
					.includes(accountEmailDomain?.toLowerCase() || ''),
			),
		);

		if (domainMatchCompanies.length > 0) {
			setSuggestions({
				companies: domainMatchCompanies,
				matchedOn: MatchType.DomainMatch,
			});

			return;
		}

		// If no companies match on domain, do a fuzzy search on company names
		const u = new uFuzzy();
		const companyNames = options.map((company) =>
			company.name.toLowerCase(),
		);
		const accountCompanyName = account?.company?.name?.toLowerCase() ?? '';

		// First, try to find a match for the full company name
		const fullNameSearchResult = u.search(companyNames, accountCompanyName);
		const hasFullNameSearchMatches =
			fullNameSearchResult[1] && fullNameSearchResult[1].idx?.length > 0;

		if (hasFullNameSearchMatches) {
			const idxs = fullNameSearchResult[1].idx;
			const matchedCompanyNames = idxs.map((idx) => companyNames[idx]);
			const matchedCompanies = options.filter((company) =>
				matchedCompanyNames.includes(company.name.toLowerCase()),
			);

			setSuggestions({
				companies: matchedCompanies,
				matchedOn: MatchType.NameMatch,
			});

			return;
		}

		// Second, try to find matches for each token in the company name
		const accountCompanyNameTokens = accountCompanyName
			.split(' ')
			.filter((t) => t.length > 2);
		const companyMatchScores = new Map<Company, number>();

		for (const token of accountCompanyNameTokens) {
			if (token.length < 2) continue;

			const tokenSearchResult = u.search(companyNames, token);
			const hasTokenSearchMatches =
				tokenSearchResult[1] && tokenSearchResult[1].idx?.length > 0;

			if (hasTokenSearchMatches) {
				const idxs = tokenSearchResult[1].idx;
				const matchedCompanies = idxs.map((idx) => options[idx]);

				const tokenScore = token.length;

				for (const company of matchedCompanies) {
					const currentScore = companyMatchScores.get(company) || 0;
					companyMatchScores.set(company, currentScore + tokenScore);
				}
			}
		}

		const nameTokenSearchResults = Array.from(companyMatchScores.entries())
			.sort((a, b) => b[1] - a[1])
			.map((entry) => entry[0]);

		if (nameTokenSearchResults.length > 0) {
			setSuggestions({
				companies: nameTokenSearchResults,
				matchedOn: MatchType.TokenMatch,
			});
		}
	};

	return {
		suggestions,
		refreshSuggestions: filter,
	};
};
